import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useDropzone} from 'react-dropzone';
import {Link} from 'react-router-dom';

export interface BatchImportErrorItem {
  row: number;
  message: string;
  data?: Record<string, unknown>;
}

export interface BatchImportResult {
  success: boolean;
  message: string;
  errors?: BatchImportErrorItem[];
  results?: {
    success?: Array<{
      id?: string;
      email?: string;
      name?: string;
      unit?: string;
      row?: number;
    }>;
    errors?: BatchImportErrorItem[];
    totalProcessed?: number;
    totalSkipped?: number;
  };
}

interface BatchImportDialogProps {
  open: boolean;
  onClose: () => void;
  onFileSelected: (file: File) => void;
  loading?: boolean;
  result?: BatchImportResult | null;
}

const BatchImportDialog: React.FC<BatchImportDialogProps> = ({
  open,
  onClose,
  onFileSelected,
  loading = false,
  result = null,
}) => {
  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
    },
    multiple: false,
    onDrop: acceptedFiles => {
      if (acceptedFiles.length > 0 && !loading) {
        onFileSelected(acceptedFiles[0]);
      }
    },
    disabled: loading,
  });

  const skippedRows = result?.results?.errors?.length
    ? result.results.errors
    : result?.errors || [];
  const importedCount = result?.results?.success?.length || 0;
  const skippedCount = skippedRows.length;
  const resultSeverity = importedCount > 0 && skippedCount === 0
    ? 'success'
    : importedCount > 0
      ? 'warning'
      : 'error';

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{m: 0, p: 2}}>
        Batch Import
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}
          disabled={loading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {result ? (
          <>
            <Alert severity={resultSeverity} sx={{mb: 2}}>
              {result.message ||
                (importedCount > 0
                  ? `Imported ${importedCount} resident${importedCount === 1 ? '' : 's'}.`
                  : 'No residents were imported.')}
            </Alert>
            {skippedCount > 0 && (
              <>
                <Typography variant="subtitle2" sx={{mb: 1}}>
                  Skipped rows — fix these and upload them again. Residents already imported were
                  not changed.
                </Typography>
                <TableContainer sx={{maxHeight: 280, mb: 1}}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell width={72}>Row</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {skippedRows.map((error, index) => (
                        <TableRow key={`${error.row}-${index}`}>
                          <TableCell>{error.row || '—'}</TableCell>
                          <TableCell>{error.message}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        ) : (
          <>
            <Typography variant="body1" sx={{mb: 1}}>
              Available formats: CSV, Excel (.xlsx, .xls).
            </Typography>
            <Typography variant="body2" sx={{mb: 2}}>
              <a
                href="https://docs.google.com/spreadsheets/d/1OQ1jRmV7X_Z9BjE_jTtvqZssyFmaHy6zsH3UuNbQEyU/edit?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
                style={{textDecoration: 'underline', color: '#1976d2', fontWeight: 500}}
              >
                Download Template
              </a>
            </Typography>
            <Alert severity="info" sx={{mb: 2}}>
              <strong>Note:</strong> Please create the apartment first before batch importing
              buildings, rooms, or residents.{' '}
              <Link
                to="/property"
                style={{
                  textDecoration: 'underline',
                  color: '#1976d2',
                  fontWeight: 500,
                  marginLeft: 4,
                }}
              >
                Go to Property Page
              </Link>
            </Alert>
            <Alert severity="info" sx={{mb: 2}}>
              From Apple Numbers, use File → Export To → CSV (or Excel). Do not rename a .numbers
              file to .csv. Rows with missing required fields are skipped; complete rows are
              imported.
            </Alert>
            <Typography variant="body2" sx={{mb: 3}}>
              Please upload a file with the required columns.
            </Typography>
            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: loading ? 'not-allowed' : 'pointer',
                bgcolor: isDragActive ? '#f0f8ff' : '#fafafa',
                transition: 'background 0.2s',
                opacity: loading ? 0.6 : 1,
                pointerEvents: loading ? 'none' : 'auto',
              }}
            >
              <input {...getInputProps()} disabled={loading} />
              <Box sx={{fontSize: 48, color: '#ccc', mb: 2}}>
                <span role="img" aria-label="spreadsheet">
                  📄
                </span>
              </Box>
              {loading ? (
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2}}>
                  <CircularProgress size={28} />
                  <Typography variant="body2" color="textSecondary" sx={{mt: 1}}>
                    Importing, please wait...
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Drag & drop file here or{' '}
                  <Button variant="text" component="span" sx={{p: 0, minWidth: 0}}>
                    choose a file
                  </Button>{' '}
                  to upload
                </Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      {result && (
        <DialogActions>
          <Button onClick={onClose} variant="contained">
            Done
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default BatchImportDialog;
